import BankOutlined from '@ant-design/icons/BankOutlined'
import CloseOutlined from '@ant-design/icons/CloseOutlined'
import PlusOutlined from '@ant-design/icons/PlusOutlined'
import TeamOutlined from '@ant-design/icons/TeamOutlined'
import UserOutlined from '@ant-design/icons/UserOutlined'
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  message,
  Modal,
  Radio,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
} from 'antd'
import { inject, observer } from 'mobx-react'
import moment from 'moment'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { blue, redPrimary } from '../../../color'
import { AvatarHasName } from '../../../components/Common/Avatar'
import RichEditor from '../../../components/RichEditor/RichEditor'
import SelectPeoplePopup from '../../../components/SelectPeoplePopup'
import { ASSIGNEE_TYPE } from '../../../constants'
import utils from '../../../utils'
import { SelectList } from '../../MessagesPage/MessagesPageStyled'

const InternalAssessmentCreate = props => {
  const {
    open,
    setOpen,
    internalAssessmentStore,
    selectPeopleStore,
    loadingAnimationStore,
    authenticationStore,
    departmentStore,
  } = props

  const {
    createRating,
    fetchAllRatings,
    updateEvaluationDetail,
  } = internalAssessmentStore

  const {
    selectUserData,
    selectDepartmentData,
    selectGroupData,
    isVisibleSelectUserDepartmentPopup,
    setIsVisibleSelectUserDepartmentPopup,
    handleOpenSelectUserDepartmentPopup,
    handleCancelSelectUserDepartment,
  } = selectPeopleStore
  const { currentUser } = authenticationStore
  const {
    officeDepartment,
    getDepartmentList,
    departmentList,
  } = departmentStore
  const [form] = Form.useForm()
  const EDITOR_REF = useRef(null)
  const [dataEditor, setdataEditor] = useState(null)

  const clearAllData = useCallback(async () => {
    await selectPeopleStore.clearSelectUserData()
    form.resetFields()
    setOpen(false)
    await selectPeopleStore.clearSelected()
  }, [selectPeopleStore, form, setOpen])

  useEffect(() => {
    ;(async () => {
      await selectPeopleStore.clearSelectUserData()
      await selectPeopleStore.clearSelected()
      await internalAssessmentStore.clearRatingDetails()
      await internalAssessmentStore.fetchAllSizeRatings()
      await getDepartmentList()
    })()
  }, [])

  const leaderList = useMemo(
    () => [
      ...selectUserData[ASSIGNEE_TYPE.LEADER],
      ...selectDepartmentData[ASSIGNEE_TYPE.LEADER],
      ...selectGroupData[ASSIGNEE_TYPE.LEADER],
    ],
    [selectUserData, selectDepartmentData, selectGroupData]
  )
  const handlerList = useMemo(
    () => [
      ...selectUserData[ASSIGNEE_TYPE.HANDLER],
      ...selectDepartmentData[ASSIGNEE_TYPE.HANDLER],
      ...selectGroupData[ASSIGNEE_TYPE.HANDLER],
    ],
    [selectUserData, selectDepartmentData, selectGroupData]
  )
  const combinerList = useMemo(
    () => [
      ...selectUserData[ASSIGNEE_TYPE.COMBINER],
      ...selectDepartmentData[ASSIGNEE_TYPE.COMBINER],
      ...selectGroupData[ASSIGNEE_TYPE.COMBINER],
    ],
    [selectUserData, selectDepartmentData, selectGroupData]
  )

  const onValuesChange = useCallback(
    (_, allValues) => {
      const fields = [
        'discipline_score',
        'skill_score',
        'performance_score',
        'bonus_score',
      ]

      const total = fields.reduce((sum, field) => {
        const value = parseFloat(allValues[field]) || 0
        return sum + value
      }, 0)

      form.setFieldsValue({ total_score: total.toString() })
    },
    [form]
  )

  const onFinish = useCallback(
    async values => {
      const {
        title,
        month,
        year,
        discipline_score,
        skill_score,
        performance_score,
        bonus_score,
        total_score,
        bonus_reason,
        bonus_amount,
        article_count,
        file_count,
        district_articles,
        english_articles,
        journalism_economics,
        published_count,
        debt_collection_rate,
        selfEvaluation,
        classification,
      } = values

      // Kiểm tra xem đã có phiếu nào trùng evaluatename, tháng và năm chưa
      const exists = internalAssessmentStore.ratingAllSize.find(
        rating =>
          rating.evaluateeName === currentUser.username &&
          Number(rating.month) === Number(month) &&
          rating.year === year.year() // sử dụng year.year() để lấy số năm
      )

      if (exists) {
        return message.info('Bạn đã tạo phiếu trong tháng này')
      }

      const truongPhongBan = leaderList?.map(el => ({
        assignee_code: el.id,
        assignee_type: el.assignee_type,
        permission: el.permission,
      }))

      const banBienTap = handlerList?.map(el => ({
        assignee_code: el.id,
        assignee_type: el.assignee_type,
        permission: el.permission,
      }))

      const vanPhong = {
        assignee_code: officeDepartment?.code,
        assignee_type: ASSIGNEE_TYPE.DEPARTMENT,
        permission: ASSIGNEE_TYPE.COMBINER,
      }

      if (truongPhongBan?.length === 0) {
        return message.info('Bạn chưa chọn Trưởng phòng ban!')
      }

      if (banBienTap?.length === 0) {
        return message.info('Bạn chưa chọn Ban biên tập!')
      }

      try {
        loadingAnimationStore.setTableLoading(true)
        const submitDataCreate = {
          title: title,
          evaluateeName: authenticationStore.currentUser.username,
          month: month,
          year: new Date(year).getFullYear(),
          assignees: [...truongPhongBan, ...banBienTap, vanPhong],
        }

        const res = await createRating(submitDataCreate)

        if (res && res.code) {
          const submitDataUpdate = {
            disciplineScore: discipline_score,
            skillScore: skill_score,
            performanceScore: performance_score,
            bonusScore: bonus_score,
            totalScore: total_score,
            bonusReason: bonus_reason,
            bonusAmount: bonus_amount,
            articleCount: article_count,
            fileCount: file_count,
            districtArticles: district_articles,
            englishArticles: english_articles,
            journalismEconomics: journalism_economics,
            publishedCount: published_count,
            debtCollectionRate: debt_collection_rate,
            selfEvaluation: EDITOR_REF?.current?.editor.getData(),
            comments: classification,
          }

          await updateEvaluationDetail(res.code, 'self', submitDataUpdate)
        } else {
          console.error('Đã có lỗi xảy ra')
        }
        setOpen(false)
        message.success('Tạo phiếu đánh giá thành công!')
        await fetchAllRatings()
        await selectPeopleStore.clearSelectUserData()
        await selectPeopleStore.clearSelected()
        clearAllData()
      } catch (error) {
        console.error('Đã có lỗi xảy ra:', error)
      } finally {
        loadingAnimationStore.setTableLoading(false)
      }
    },
    [
      leaderList,
      handlerList,
      combinerList,
      authenticationStore,
      createRating,
      updateEvaluationDetail,
      fetchAllRatings,
      selectPeopleStore,
      clearAllData,
      loadingAnimationStore,
    ]
  )

  const UserSelect = useCallback(
    ({ label, assigneeType, list, onButtonClick, onRemove }) => {
      return (
        <Col span={12}>
          <label
            style={{ display: 'inline-block', margin: '15px 12px 25px 0' }}>
            <span style={{ color: redPrimary, marginRight: 4 }}>*</span>
            {label}
          </label>
          <Space>
            <Tooltip title="Chọn người dùng" color={blue}>
              <Button
                icon={<UserOutlined style={{ color: blue }} />}
                onClick={() => onButtonClick(assigneeType)}
              />
            </Tooltip>
          </Space>
          <SelectList>
            {list.map(el => (
              <Tag
                className="tag-selected"
                color="blue"
                key={el.id}
                closable
                onClose={() => onRemove(el)}>
                <AvatarHasName
                  imgId={el.image_id}
                  size={22}
                  name={el.full_name}
                  icon={
                    (el.assignee_type === ASSIGNEE_TYPE.DEPARTMENT && (
                      <BankOutlined />
                    )) ||
                    (el.assignee_type === ASSIGNEE_TYPE.GROUP && (
                      <TeamOutlined />
                    ))
                  }
                />
              </Tag>
            ))}
          </SelectList>
        </Col>
      )
    },
    []
  )

  const columns = useMemo(
    () => [
      {
        title: 'TT',
        dataIndex: 'key',
        width: 50,
        align: 'center',
      },
      {
        title: 'Nội dung tiêu chí chấm điểm',
        dataIndex: 'content',
        width: 300,
        render: (text, record) => {
          // Make the "Cộng điểm" row bold
          return record.key === '5' ? <strong>{text}</strong> : text
        },
      },
      {
        title: 'Điểm tối đa',
        dataIndex: 'maxPoints',
        width: 100,
        align: 'center',
        render: (value, record, index) => {
          // Chỉ hiển thị "Điểm tối đa" cho 5 hàng đầu tiên
          if (index >= 5) {
            return {
              children: null,
              props: { colSpan: 0 }, // Ẩn cột "Điểm tối đa" từ hàng thứ 6
            }
          }
          return record.key === '5' ? <strong>{value}</strong> : value
        },
      },
      {
        title: 'Cá nhân tự đánh giá',
        dataIndex: 'selfEvaluation',
        width: 200,
        align: 'center',
        render: (_, record, index) => {
          if (index >= 5) {
            return {
              children: (
                <Form.Item name={record.label} style={{ margin: 0 }}>
                  <Input
                    placeholder={`Nhập ${record.content.toLowerCase()}`}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              ),
              props: { colSpan: 2 },
            }
          }

          if (index === 4) {
            // Trường tổng điểm
            return (
              <Form.Item name={record.label} style={{ margin: 0 }}>
                <Input
                  readOnly
                  style={{
                    width: '100%',
                    backgroundColor: '#f5f5f5',
                    fontWeight: 'bold',
                  }}
                />
              </Form.Item>
            )
          }

          // Các trường nhập điểm
          return (
            <Form.Item
              name={record.label}
              style={{ margin: 0 }}
              initialValue={0}
              rules={[
                {
                  type: 'number',
                  transform: value => Number(value),
                  min: 0,
                  max: record.maxPoints,
                  message: `Điểm phải nằm trong khoảng từ 0 đến ${record.maxPoints}`,
                },
              ]}>
              <Input
                type="number"
                style={{ width: '100%' }}
                min={0}
                max={record.maxPoints}
              />
            </Form.Item>
          )
        },
      },
    ],
    []
  )

  const data = useMemo(
    () => [
      {
        key: '1',
        label: 'discipline_score',
        content: 'Ý thức tổ chức kỷ luật',
        maxPoints: 20,
      },
      {
        key: '2',
        label: 'skill_score',
        content: 'Năng lực, Kỹ năng',
        maxPoints: 20,
      },
      {
        key: '3',
        label: 'performance_score',
        content: 'Kết quả thực hiện nhiệm vụ được giao',
        maxPoints: 50,
      },
      {
        key: '4',
        label: 'bonus_score',
        content: 'Điểm thưởng',
        maxPoints: 10,
      },
      {
        key: '5',
        label: 'total_score',
        content: 'Cộng điểm',
        maxPoints: 100,
      },
      {
        key: '6',
        label: 'bonus_reason',
        content: 'Lý do thưởng',
      },
      {
        key: '7',
        label: 'bonus_amount',
        content: 'Nhuận bút (đồng)',
      },
      {
        key: '8',
        label: 'article_count',
        content: 'Số lượng tin, bài',
      },
      {
        key: '9',
        label: 'file_count',
        content: 'Số đề tài/ Phóng sự',
      },
      {
        key: '10',
        label: 'district_articles',
        content: 'Bài viết về quận, huyện, ngành theo dõi',
      },
      {
        key: '11',
        label: 'english_articles',
        content: 'Bài viết chuyên sâu bằng tiếng Anh(PV.HNT)',
      },
      {
        key: '12',
        label: 'journalism_economics',
        content: 'Kinh tế báo chí + Đạt %',
      },
      {
        key: '13',
        label: 'published_count',
        content: 'Số lượng báo phát hành',
      },
      {
        key: '14',
        label: 'debt_collection_rate',
        content: 'Tỷ lệ thu hồi công nợ',
      },
    ],
    []
  )

  const handleMonthChange = value => {
    const newTitle = `Đánh giá xếp loại CB,VC,NLĐ tháng ${value} - ${
      utils.getNameInCapitalize(
        authenticationStore.currentUser?.name_lowercase
      ) || ''
    }`
    form.setFieldsValue({ title: newTitle })
  }

  const defaultTitle = `Đánh giá xếp loại CB,VC,NLĐ tháng ${moment().format(
    'M'
  )} - ${utils.getNameInCapitalize(currentUser?.name_lowercase) || ''}`

  return (
    <>
      <Modal
        width={800}
        visible={open}
        onCancel={clearAllData}
        title={'Tạo mới phiếu đánh giá nội bộ'}
        style={{ top: 5 }}
        maskClosable={false}
        footer={null}>
        <Form
          form={form}
          onFinish={onFinish}
          layout="vertical"
          onValuesChange={onValuesChange}
          style={{ maxWidth: '800px', margin: '0 auto' }}
          initialValues={{
            title: defaultTitle,
            month: new Date().getMonth() + 1,
            year: moment(),
          }}>
          <Form.Item
            label="Tiêu đề"
            name="title"
            rules={[{ required: true, message: 'Vui lòng nhập tiêu đề!' }]}>
            <Input placeholder="Nhập tiêu đề" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Chọn tháng"
                name="month"
                rules={[{ required: true, message: 'Vui lòng chọn tháng!' }]}>
                <Select
                  style={{ width: '100%' }}
                  placeholder="Chọn tháng"
                  onChange={handleMonthChange}>
                  {[
                    { name: 'Tháng 1', number: 1 },
                    { name: 'Tháng 2', number: 2 },
                    { name: 'Tháng 3', number: 3 },
                    { name: 'Tháng 4', number: 4 },
                    { name: 'Tháng 5', number: 5 },
                    { name: 'Tháng 6', number: 6 },
                    { name: 'Tháng 7', number: 7 },
                    { name: 'Tháng 8', number: 8 },
                    { name: 'Tháng 9', number: 9 },
                    { name: 'Tháng 10', number: 10 },
                    { name: 'Tháng 11', number: 11 },
                    { name: 'Tháng 12', number: 12 },
                  ].map((month, index) => (
                    <Select.Option key={index} value={month.number}>
                      {month.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Chọn năm"
                name="year"
                rules={[{ required: true, message: 'Vui lòng chọn năm!' }]}>
                <DatePicker style={{ width: '100%' }} picker="year" />
              </Form.Item>
            </Col>
          </Row>

          <Table
            columns={columns}
            dataSource={data}
            pagination={false}
            bordered
            size="middle"
            scroll={{ x: 'max-content' }}
          />
          <Form.Item
            label="Tự đánh giá các việc đã làm được và những hạn chế chưa làm được"
            name="selfEvaluation"
            style={{ marginTop: '10px' }}>
            <RichEditor
              EDITOR_REF={EDITOR_REF}
              editorContent={dataEditor}
              height={300}
            />
          </Form.Item>

          {/* Radio group for classification */}
          <Form.Item
            label="Tự xếp loại"
            name="classification"
            rules={[{ required: true, message: 'Vui lòng chọn xếp loại!' }]}>
            <Radio.Group
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
              }}>
              <Radio
                value="excellent"
                style={{
                  padding: '8px',
                  border: '1px solid #d9d9d9',
                  borderRadius: '4px',
                  backgroundColor: '#fafafa',
                  color: 'red',
                  margin: 0,
                }}>
                Xuất sắc (Từ 90 đến 100 điểm)
              </Radio>
              <Radio
                value="good"
                style={{
                  padding: '8px',
                  border: '1px solid #d9d9d9',
                  borderRadius: '4px',
                  backgroundColor: '#fafafa',
                  color: 'blue',
                  margin: 0,
                }}>
                Tốt (Từ 70 đến 89 điểm)
              </Radio>
              <Radio
                value="completed"
                style={{
                  padding: '8px',
                  border: '1px solid #d9d9d9',
                  borderRadius: '4px',
                  backgroundColor: '#fafafa',
                  margin: 0,
                }}>
                Hoàn thành (Từ 50 đến dưới 69 điểm)
              </Radio>
              <Radio
                value="notCompleted"
                style={{
                  padding: '8px',
                  border: '1px solid #d9d9d9',
                  borderRadius: '4px',
                  backgroundColor: '#fafafa',
                  margin: 0,
                }}>
                Không hoàn thành (Dưới 50 điểm)
              </Radio>
            </Radio.Group>
          </Form.Item>
          <Row gutter={[16, 16]}>
            <UserSelect
              label="Trưởng phòng/ban:"
              assigneeType={ASSIGNEE_TYPE.LEADER}
              list={leaderList}
              onButtonClick={handleOpenSelectUserDepartmentPopup}
              onRemove={selectPeopleStore.handleRemoveSelect}
            />
            {/* <UserSelect
              label="Văn phòng:"
              assigneeType={ASSIGNEE_TYPE.COMBINER}
              list={combinerList}
              onButtonClick={handleOpenSelectUserDepartmentPopup}
              onRemove={selectPeopleStore.handleRemoveSelect}
            /> */}
            <UserSelect
              label="Ban biên tập:"
              assigneeType={ASSIGNEE_TYPE.HANDLER}
              list={handlerList}
              onButtonClick={handleOpenSelectUserDepartmentPopup}
              onRemove={selectPeopleStore.handleRemoveSelect}
            />
          </Row>
          <Form.Item
            style={{
              margin: 0,
              width: '100%',
              marginTop: '15px',
            }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
              }}>
              <Button
                danger
                icon={<CloseOutlined />}
                onClick={clearAllData}
                style={{ marginLeft: '10px' }}>
                Hủy bỏ
              </Button>
              <Button
                icon={<PlusOutlined />}
                type="primary"
                htmlType="submit"
                loading={loadingAnimationStore.isLoading}
                style={{ marginLeft: '10px' }}>
                Tạo mới
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>
      <SelectPeoplePopup
        handleCancelSelectUser={handleCancelSelectUserDepartment}
        handleSubmitSelectUser={() =>
          setIsVisibleSelectUserDepartmentPopup(false)
        }
        isVisibleSelectPeoplePopup={isVisibleSelectUserDepartmentPopup}
        departmentType={
          selectPeopleStore.assigneePopupType === ASSIGNEE_TYPE.LEADER
            ? currentUser?.department?.code
            : selectPeopleStore.assigneePopupType === ASSIGNEE_TYPE.COMBINER
            ? 'DPT182746500449800'
            : 'DPT10362627906695166'
        }
        disableAllDisabling={true}
      />
    </>
  )
}

export default inject(
  'internalAssessmentStore',
  'selectPeopleStore',
  'loadingAnimationStore',
  'authenticationStore',
  'departmentStore'
)(observer(InternalAssessmentCreate))
